import { type SigningLane, signingLane } from './signingChannel'

const DEFAULT_EHR_TITLE = 'Your order is ready to submit'
const DEFAULT_EHR_BODY = 'This order will be sent to the lab for analysis when you submit.'

const REQUEST_SIGNATURE_TITLE = 'Ready to Request Signature'
const REQUEST_SIGNATURE_CTA = 'Request Signature'

const EMBEDDED_TITLE = 'This order is ready to submit and sign'
const EMBEDDED_BODY = "When you continue, we'll prepare the requisition form for you to sign here in the app."
const EMBEDDED_CTA = 'Continue to Signing'

const EMBEDDED_OTHER_BODY =
  'After you submit, the authorizing clinician will receive an email with a link to sign the requisition form in Inflection.'

/**
 * 'docusign' for every DocuSign lane so the live page gate
 * `signingCopy.treatment === 'docusign'` keeps matching without change.
 * Use `lane` to distinguish them within the DocuSign path.
 */
export type SigningCopyTreatment = 'ehr' | 'docusign'

/** Narrows the DocuSign treatment to the specific sending lane. */
export type SigningCopyLane = SigningLane

export interface SigningCopyInput {
  /** Boolean result of isTrfSignatureDocusignEnabled(config). Must be real boolean true to activate DocuSign copy. */
  docusignEnabled: boolean
  /**
   * True when a real authorizing clinician is the attestor
   * (`selectedAuthorizingProvider != null`); false otherwise.
   */
  submittingOnBehalfOf: boolean
  /** providerConsent.ehrSignReminderTitle — applied only when DocuSign is off. */
  ehrTitle?: string | null
  /** providerConsent.ehrSignReminderBody — applied only when DocuSign is off. */
  ehrBody?: string | null
}

export interface SigningCopyResult {
  title: string
  body: string
  /**
   * 'docusign' for any DocuSign-on order (every lane but 'ehr');
   * 'ehr' when DocuSign is off. Matches the live page gate
   * `signingCopy.treatment === 'docusign'`.
   */
  treatment: SigningCopyTreatment
  /**
   * Narrows the DocuSign treatment to its specific lane; 'ehr' when DocuSign is off.
   * Use this — not `treatment` — to choose CTA label, post-submit modal, or
   * navigation in downstream cells.
   */
  lane: SigningCopyLane
  /**
   * Label for the primary submit CTA, or null when the caller should keep
   * its own default submit label (EHR path).
   */
  ctaLabel: string | null
}

/**
 * Pure function: derives the top-banner copy, visual treatment, lane, and CTA
 * label for the Order Ready to Send page. See {@link signingLane} for the lanes.
 */
export function readyToSendSigningCopy(input: SigningCopyInput): SigningCopyResult {
  const { docusignEnabled, submittingOnBehalfOf, ehrTitle, ehrBody } = input

  const lane = signingLane({ docusignEnabled, submittingOnBehalfOf })

  switch (lane) {
    case 'ehr':
      return {
        title: ehrTitle?.trim() || DEFAULT_EHR_TITLE,
        body: ehrBody?.trim() || DEFAULT_EHR_BODY,
        treatment: 'ehr',
        lane,
        ctaLabel: null,
      }
    case 'embedded-other':
      return {
        title: REQUEST_SIGNATURE_TITLE,
        body: EMBEDDED_OTHER_BODY,
        treatment: 'docusign',
        lane,
        ctaLabel: REQUEST_SIGNATURE_CTA,
      }
    case 'embedded':
      return {
        title: EMBEDDED_TITLE,
        body: EMBEDDED_BODY,
        treatment: 'docusign',
        lane,
        ctaLabel: EMBEDDED_CTA,
      }
  }
}
